import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getAuthDepartments, type AuthDepartmentPerson } from '@/api/auth';
import { submitDivideCart, type CartSubmitSpec } from '@/api/cart';
import SearchableSelect from '@/components/base/SearchableSelect';
import OrderItemsTable, {
  ensureSingleTrailingEmpty,
  isEmptyOrderItem,
  type OrderItem,
  type ProductPickPayload,
} from '@/pages/order/components/OrderItemsTable';
import ProductSelector, { type SelectableProduct } from '@/pages/order/components/ProductSelector';

interface FormErrors {
  department?: string;
  person?: string;
  items?: string;
}

/** 将清单行转为 CartSubmitSpec；不符合则返回错误文案 */
function buildCartSpecs(items: OrderItem[]): { specs: CartSubmitSpec[] } | { error: string } {
  const filled = items.filter((item) => !isEmptyOrderItem(item));
  if (filled.length === 0) {
    return { error: '请至少添加一件商品' };
  }

  const specs: CartSubmitSpec[] = [];
  for (const item of filled) {
    const id = Number(item.specId);
    const goodsId = Number(item.productId);
    const quantity = Math.floor(Number(item.quantity));
    const goodsName = String(item.name || '').trim();
    const specName = String(item.spec || '').trim();

    if (!Number.isFinite(id) || id <= 0) {
      return { error: `商品「${goodsName || '未命名'}」缺少有效规格，请重新选择` };
    }
    if (!Number.isFinite(goodsId) || goodsId <= 0 || !goodsName) {
      return { error: '请完善商品信息后再提交' };
    }
    if (!Number.isFinite(quantity) || quantity < 1) {
      return { error: `商品「${goodsName}」数量必须为大于 0 的整数` };
    }

    specs.push({
      id,
      name: specName || goodsName,
      goods_id: goodsId,
      goods_name: goodsName,
      quantity,
      remark: item.itemRemark?.trim() || undefined,
    });
  }

  return { specs };
}

export default function ProxyOrder() {
  const navigate = useNavigate();

  const [deptOptions, setDeptOptions] = useState<string[]>([]);
  const [deptIdMap, setDeptIdMap] = useState<Record<string, string>>({});
  const [personsByDept, setPersonsByDept] = useState<Record<string, AuthDepartmentPerson[]>>({});
  const [selectedDept, setSelectedDept] = useState('');
  const [selectedPerson, setSelectedPerson] = useState('');
  const [orderRemark, setOrderRemark] = useState('');
  const [errors, setErrors] = useState<FormErrors>({});

  const [editableItems, setEditableItems] = useState<OrderItem[]>(() =>
    ensureSingleTrailingEmpty([]),
  );
  const [showProductSelector, setShowProductSelector] = useState(false);
  const [lastItemError, setLastItemError] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null);

  const showToast = useCallback((message: string, type: 'success' | 'error' = 'success') => {
    setToast({ message, type });
    window.setTimeout(() => setToast(null), 3000);
  }, []);

  // 科室列表：/platform/auth/departments（含 persons）
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const res = await getAuthDepartments();
        const ok = res.error === false || Number(res.error) === 0;
        if (cancelled || !ok || !Array.isArray(res.data)) return;

        const nextIdMap: Record<string, string> = {};
        const nextPersons: Record<string, AuthDepartmentPerson[]> = {};
        const nextOptions: string[] = [];

        res.data.forEach((item) => {
          const id = Number(item.id);
          const title = String(item.title || '').trim();
          if (!title || !Number.isFinite(id) || id <= 0) return;
          if (nextIdMap[title]) return;

          nextIdMap[title] = String(id);
          nextOptions.push(title);
          nextPersons[title] = Array.isArray(item.persons)
            ? item.persons
                .map((p) => ({
                  id: p.id,
                  account: p.account,
                  name: String(p.name || p.account || '').trim(),
                  phone: p.phone,
                }))
                .filter((p) => !!p.name)
            : [];
        });

        setDeptIdMap(nextIdMap);
        setDeptOptions(nextOptions);
        setPersonsByDept(nextPersons);
      } catch {
        if (!cancelled) {
          setDeptOptions([]);
          setDeptIdMap({});
          setPersonsByDept({});
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const personOptions = useMemo(() => {
    if (!selectedDept) return [];
    return (personsByDept[selectedDept] || []).map((p) => p.name);
  }, [selectedDept, personsByDept]);

  const selectedPersonId = useMemo(() => {
    if (!selectedDept || !selectedPerson) return '';
    const hit = (personsByDept[selectedDept] || []).find((p) => p.name === selectedPerson);
    return hit ? String(hit.id) : '';
  }, [selectedDept, selectedPerson, personsByDept]);

  const validateForm = useCallback((): FormErrors => {
    const next: FormErrors = {};
    const deptId = selectedDept ? deptIdMap[selectedDept] : '';
    if (!selectedDept || !deptId) {
      next.department = '请选择科室部门';
    }
    if (!selectedPerson || !selectedPersonId) {
      next.person = selectedDept ? '请选择下单人' : '请先选择科室部门';
    }

    const built = buildCartSpecs(editableItems);
    if ('error' in built) {
      next.items = built.error;
    }

    return next;
  }, [selectedDept, selectedPerson, selectedPersonId, deptIdMap, editableItems]);

  const handleDeptChange = (value: string | string[]) => {
    const next = Array.isArray(value) ? value[0] || '' : value;
    setSelectedDept(next);
    const persons = next ? personsByDept[next] || [] : [];
    const firstPerson = persons[0]?.name || '';
    setSelectedPerson(firstPerson);
    setErrors((prev) => ({
      ...prev,
      department: next ? undefined : prev.department,
      person: firstPerson ? undefined : prev.person,
    }));
  };

  const handlePersonChange = (value: string | string[]) => {
    const next = Array.isArray(value) ? value[0] || '' : value;
    setSelectedPerson(next);
    setErrors((prev) => ({
      ...prev,
      person: next ? undefined : prev.person,
    }));
  };

  const handleUpdateQuantity = (index: number, quantity: number) => {
    setEditableItems((prev) => {
      const next = [...prev];
      next[index] = { ...next[index], quantity };
      return next;
    });
    setErrors((prev) => (prev.items ? { ...prev, items: undefined } : prev));
  };

  const handleUpdateRemark = (index: number, remark: string) => {
    setEditableItems((prev) => {
      const next = [...prev];
      next[index] = { ...next[index], itemRemark: remark };
      return next;
    });
  };

  const handleUpdateSpec = (
    index: number,
    specId: string,
    price: number,
    model: string,
    unit?: string,
  ) => {
    const duplicated = editableItems.some(
      (item, i) => i !== index && !isEmptyOrderItem(item) && item.specId && item.specId === specId,
    );
    if (duplicated) {
      showToast('该商品规格已在列表中，同一规格只能使用一次', 'error');
      return;
    }
    setEditableItems((prev) => {
      const next = [...prev];
      next[index] = {
        ...next[index],
        specId,
        unitPrice: price,
        spec: model,
        ...(unit ? { unit } : {}),
      };
      return next;
    });
    setErrors((prev) => (prev.items ? { ...prev, items: undefined } : prev));
    showToast(`规格已更新为「${model}」`);
  };

  const handleUpdateProduct = (index: number, product: ProductPickPayload) => {
    const duplicated = editableItems.some(
      (item, i) =>
        i !== index && !isEmptyOrderItem(item) && item.specId && item.specId === product.specId,
    );
    if (duplicated) {
      showToast('该商品规格已在列表中，同一规格只能使用一次', 'error');
      return;
    }
    setEditableItems((prev) => {
      const next = [...prev];
      next[index] = {
        ...next[index],
        productId: product.productId,
        name: product.name,
        image: product.image || '',
        specId: product.specId,
        spec: product.spec,
        unitPrice: product.unitPrice,
        unit: product.unit,
        quantity: next[index].quantity > 0 ? next[index].quantity : 1,
        specOptions: product.specs || [],
      };
      return ensureSingleTrailingEmpty(next);
    });
    setErrors((prev) => (prev.items ? { ...prev, items: undefined } : prev));
    showToast(`已更换为「${product.name}」并联动规格`);
  };

  const handleDeleteItem = (index: number) => {
    const target = editableItems[index];
    const filledCount = editableItems.filter((item) => !isEmptyOrderItem(item)).length;
    if (target && !isEmptyOrderItem(target) && filledCount <= 1) {
      setLastItemError(true);
      window.setTimeout(() => setLastItemError(false), 3000);
      return;
    }
    setEditableItems((prev) => ensureSingleTrailingEmpty(prev.filter((_, i) => i !== index)));
    setErrors((prev) => (prev.items ? { ...prev, items: undefined } : prev));
  };

  const handleAddProduct = (product: SelectableProduct) => {
    const existingIndex = editableItems.findIndex(
      (item) =>
        !isEmptyOrderItem(item) &&
        (item.specId === product.id || (!item.specId && item.productId === product.goodsId)),
    );
    if (existingIndex >= 0) {
      setEditableItems((prev) => {
        const next = [...prev];
        next[existingIndex] = {
          ...next[existingIndex],
          quantity: next[existingIndex].quantity + 1,
        };
        return next;
      });
      setErrors((prev) => (prev.items ? { ...prev, items: undefined } : prev));
      showToast(`"${product.name}" 数量已 +1`);
      return;
    }

    const newItem: OrderItem = {
      productId: product.goodsId,
      name: product.name,
      quantity: 1,
      unitPrice: product.price,
      unit: product.unit,
      specId: product.id,
      spec: product.model || '',
      image: product.image || '',
      specOptions: [
        {
          id: product.id,
          name: product.model || '',
          price: product.price,
          unit: product.unit,
        },
      ],
    };

    setEditableItems((prev) => {
      const emptyIndex = prev.findIndex((item) => isEmptyOrderItem(item));
      const next =
        emptyIndex >= 0
          ? (() => {
              const copy = [...prev];
              copy[emptyIndex] = newItem;
              return copy;
            })()
          : [...prev, newItem];
      return ensureSingleTrailingEmpty(next);
    });
    setErrors((prev) => (prev.items ? { ...prev, items: undefined } : prev));
    showToast(`已添加 "${product.name}"`);
  };

  const handleReset = () => {
    setEditableItems(ensureSingleTrailingEmpty([]));
    setOrderRemark('');
    setLastItemError(false);
    setSelectedDept('');
    setSelectedPerson('');
    setErrors({});
  };

  const handleSubmit = async () => {
    if (submitting) return;

    const nextErrors = validateForm();
    setErrors(nextErrors);
    if (nextErrors.items) {
      setLastItemError(true);
      window.setTimeout(() => setLastItemError(false), 3000);
    }
    if (Object.keys(nextErrors).length > 0) {
      showToast(
        nextErrors.department || nextErrors.person || nextErrors.items || '请完善表单',
        'error',
      );
      return;
    }

    const built = buildCartSpecs(editableItems);
    if ('error' in built) {
      setErrors({ items: built.error });
      showToast(built.error, 'error');
      return;
    }

    const deptId = Number(deptIdMap[selectedDept]);
    const accountId = Number(selectedPersonId);

    setSubmitting(true);
    try {
      const res = await submitDivideCart({
        department_id: deptId,
        account_id: accountId,
        remark: orderRemark.trim() || undefined,
        specs: built.specs,
      });
      const ok = res.error === false || Number(res.error) === 0;
      if (!ok) {
        showToast(res.message || res.msg || '代购下单失败', 'error');
        return;
      }
      showToast(res.message || '代购下单成功');
      handleReset();
      navigate('/orders');
    } catch {
      showToast('网络异常，代购下单失败', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="flex-1 bg-background-100">
      {toast && (
        <div
          className={`fixed top-4 left-1/2 -translate-x-1/2 z-[130] px-4 py-2.5 rounded-lg text-sm font-medium shadow-lg flex items-center gap-2 ${
            toast.type === 'success'
              ? 'bg-accent-500 text-background-50'
              : 'bg-red-500 text-white'
          }`}
        >
          <i
            className={
              toast.type === 'success' ? 'ri-checkbox-circle-line' : 'ri-error-warning-line'
            }
          />
          {toast.message}
        </div>
      )}

      <div className="px-4 md:px-6 pb-4 md:pb-6">
        <OrderItemsTable
          items={editableItems}
          editMode
          onUpdateQuantity={handleUpdateQuantity}
          onUpdateSpec={handleUpdateSpec}
          onUpdateProduct={handleUpdateProduct}
          onUpdateRemark={handleUpdateRemark}
          onDeleteItem={handleDeleteItem}
          onAddItem={() => setShowProductSelector(true)}
          lastItemError={lastItemError || !!errors.items}
          headerLeft={
            <div className="flex items-start gap-3 flex-wrap">
              <div className="flex flex-col gap-1 min-w-0">
                <div className="flex items-center gap-2 min-w-0">
                  <span className="text-sm text-red-500 shrink-0">*</span>
                  <span className="text-sm text-foreground-700 shrink-0 whitespace-nowrap">
                    科室部门：
                  </span>
                  <div className="w-[180px]">
                    <SearchableSelect
                      options={deptOptions}
                      value={selectedDept}
                      onChange={handleDeptChange}
                      placeholder="请选择科室"
                      allLabel="请选择科室"
                      searchPlaceholder="搜索科室..."
                      icon="ri-building-line"
                      invalid={!!errors.department}
                    />
                  </div>
                </div>
                {errors.department && (
                  <p className="text-xs text-red-600 pl-[4.5rem] flex items-center gap-1">
                    <i className="ri-error-warning-line" />
                    {errors.department}
                  </p>
                )}
              </div>
              <div className="flex flex-col gap-1 min-w-0">
                <div className="flex items-center gap-2 min-w-0">
                  <span className="text-sm text-red-500 shrink-0">*</span>
                  <span className="text-sm text-foreground-700 shrink-0 whitespace-nowrap">
                    下单人：
                  </span>
                  <div className="w-[160px]">
                    <SearchableSelect
                      options={personOptions}
                      value={selectedPerson}
                      onChange={handlePersonChange}
                      placeholder={selectedDept ? '请选择下单人' : '请先选择科室'}
                      allLabel="请选择下单人"
                      searchPlaceholder="搜索下单人..."
                      icon="ri-user-line"
                      invalid={!!errors.person}
                    />
                  </div>
                </div>
                {errors.person && (
                  <p className="text-xs text-red-600 pl-[3.75rem] flex items-center gap-1">
                    <i className="ri-error-warning-line" />
                    {errors.person}
                  </p>
                )}
              </div>
            </div>
          }
          headerActions={
            <button
              type="button"
              onClick={() => setShowProductSelector(true)}
              className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium rounded-md bg-background-100 hover:bg-background-200 text-foreground-700 border border-background-300 transition-colors whitespace-nowrap"
            >
              <i className="ri-add-line" />
              添加商品
            </button>
          }
          footerExtra={
            <div className="space-y-4">
              {errors.items && (
                <div className="px-3 py-2 rounded-lg border border-red-200 bg-red-50 text-xs text-red-600 flex items-center gap-1.5">
                  <i className="ri-error-warning-line" />
                  {errors.items}
                </div>
              )}
              <div>
                <label className="text-sm font-medium text-foreground-800 mb-2 block">
                  订单备注
                </label>
                <textarea
                  value={orderRemark}
                  disabled={submitting}
                  onChange={(e) => setOrderRemark(e.target.value.slice(0, 255))}
                  rows={3}
                  maxLength={255}
                  placeholder="代购原因、特殊要求等（选填）"
                  className="w-full px-3 py-2.5 rounded-lg border border-background-300 text-sm outline-none resize-none transition-colors focus:border-primary-400 disabled:bg-background-100"
                />
                <p className="text-xs text-foreground-400 mt-1.5 text-right">
                  {orderRemark.length}/255
                </p>
              </div>
              <div className="flex items-center justify-end gap-3 pt-1">
                <button
                  type="button"
                  disabled={submitting}
                  onClick={handleReset}
                  className="px-4 py-2 rounded-lg border border-background-300 text-sm text-foreground-600 hover:bg-background-100 whitespace-nowrap disabled:opacity-50"
                >
                  重置
                </button>
                <button
                  type="button"
                  disabled={submitting}
                  onClick={() => void handleSubmit()}
                  className="px-5 py-2 rounded-lg bg-primary-500 hover:bg-primary-600 text-background-50 text-sm font-medium disabled:opacity-50 whitespace-nowrap inline-flex items-center gap-1.5"
                >
                  {submitting ? <i className="ri-loader-4-line animate-spin" /> : null}
                  {submitting ? '提交中...' : '提交订单'}
                </button>
              </div>
            </div>
          }
        />
      </div>

      <ProductSelector
        isOpen={showProductSelector}
        onClose={() => setShowProductSelector(false)}
        onSelect={handleAddProduct}
        existingProductIds={editableItems.map((item) => item.specId).filter(Boolean) as string[]}
      />
    </div>
  );
}
